import { useEffect } from "react";
import { ThemeProvider } from "@/shared/components/ui/themeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router";
import { Toaster } from "@/shared/components/ui/sonner";
import { BrandingProvider } from "@/shared/components/BrandingProvider";
import Index from "./pages/Index";
import NotFound from "@/pages/404/NotFound";
import VideoCallPage from "./pages/VideoCallPage";
import { useCurrentUser } from "./features/login/hooks/useCurrentUser";
import { LoginPage } from "./features/login/components/LoginPage";
import { ActivateAccountPage } from "./pages/ActivateAccountPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import Actividad from "./features/actividad/components/Actividad";
import InstruccionesActividad from "./features/programas/components/InstruccionesActividad";
import { RoleGuard } from "@/shared/components/guards/RoleGuard";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { ForceLogoutPage } from "@/pages/ForceLogoutPage";
import { axiosInstance } from "@retro-ia/shared/axios";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, logout: clearUser } = useCurrentUser();
  const navigate = useNavigate();

  // RequireAuth solo verifica si hay usuario en Redux/localStorage, pero no sabe
  // si el token JWT en la cookie ya expiró. Este efecto llama a /auth/verify al
  // montar la ruta protegida para detectar sesiones expiradas sin esperar a que
  // falle una petición de datos, cerrando el gap entre cambio de ruta y próxima API call.
  useEffect(() => {
    if (!user) return;
    axiosInstance.get("/auth/verify").catch(() => {
      clearUser();
      navigate("/login", { replace: true });
    });
  }, []);

  return user ? <>{children}</> : <Navigate to="/" replace />;
}

function RoleRedirector() {
  const { user } = useCurrentUser();
  if (!user) return <Navigate to="/login" replace />;
  switch (user.rol.nombre) {
    case "APRENDIZ":
      return <Navigate to="/programas" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <Toaster position="top-center" richColors />
          <BrowserRouter>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<RoleRedirector />} />
              <Route
                path="/programas"
                element={
                  <RequireAuth>
                    <RoleGuard allowedRoles={['APRENDIZ']}>
                      <Index />
                    </RoleGuard>
                  </RequireAuth>
                }
              />
              <Route
                path="/instrucciones-actividad"
                element={
                  <RequireAuth>
                    <RoleGuard allowedRoles={['APRENDIZ']}>
                      <InstruccionesActividad />
                    </RoleGuard>
                  </RequireAuth>
                }
              />
              <Route
                path="/actividad/:id"
                element={
                  <RequireAuth>
                    <RoleGuard allowedRoles={['APRENDIZ']}>
                      <ErrorBoundary>
                        <Actividad />
                      </ErrorBoundary>
                    </RoleGuard>
                  </RequireAuth>
                }
              />
              <Route
                path="/videollamada/:sessionId"
                element={
                  <RequireAuth>
                    <RoleGuard allowedRoles={['APRENDIZ']}>
                      <VideoCallPage />
                    </RoleGuard>
                  </RequireAuth>
                }
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/clear-session" element={<ForceLogoutPage />} />
              <Route
                path="/activate/:token"
                element={<ActivateAccountPage />}
              />
              <Route
                path="/reset-password/:token"
                element={<ResetPasswordPage />}
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </BrandingProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
export default App;
